import Phaser from 'phaser';

// === GESTIONNAIRE MUSICAL SINGLETON ===
// Gère la musique du jeu : sélection aléatoire de variante, crossfade, jingles.
// Persistence du volume et de l'état ON/OFF dans localStorage.

export type MusicCategory = 'menu' | 'camp' | 'map' | 'combat' | 'boss' | 'victory' | 'defeat';

const MUSIC_KEYS: Record<MusicCategory, [string, string]> = {
  menu:    ['music-menu-1', 'music-menu-2'],
  camp:    ['music-camp-1', 'music-camp-2'],
  map:     ['music-map-1', 'music-map-2'],
  combat:  ['music-combat-1', 'music-combat-2'],
  boss:    ['music-boss-1', 'music-boss-2'],
  victory: ['music-victory-1', 'music-victory-2'],
  defeat:  ['music-defeat-1', 'music-defeat-2'],
};

const ENABLED_KEY = 'deadtide_music_enabled';
const VOLUME_KEY = 'deadtide_music_volume';
const SFX_VOLUME_KEY = 'deadtide_sfx_volume';

/** Pistes audio à charger dans BootScene.preload() */
export const MUSIC_TRACKS = [
  { key: 'music-menu-1', path: 'assets/audio/music/The Cursed Shores 1.mp3' },
  { key: 'music-menu-2', path: 'assets/audio/music/The Cursed Shores 2.mp3' },
  { key: 'music-camp-1', path: 'assets/audio/music/Safe Harbor 1.mp3' },
  { key: 'music-camp-2', path: 'assets/audio/music/Safe Harbor 2.mp3' },
  { key: 'music-map-1', path: 'assets/audio/music/Charting the Unknown 1.mp3' },
  { key: 'music-map-2', path: 'assets/audio/music/Charting the Unknown 2.mp3' },
  { key: 'music-combat-1', path: "assets/audio/music/Dead Man's Fight 1.mp3" },
  { key: 'music-combat-2', path: "assets/audio/music/Dead Man's Fight 2.mp3" },
  { key: 'music-boss-1', path: "assets/audio/music/Leviathan's Wrath 1.mp3" },
  { key: 'music-boss-2', path: "assets/audio/music/Leviathan's Wrath 2.mp3" },
  { key: 'music-victory-1', path: 'assets/audio/music/Tides of Fortune 1.mp3' },
  { key: 'music-victory-2', path: 'assets/audio/music/Tides of Fortune 2.mp3' },
  { key: 'music-defeat-1', path: 'assets/audio/music/Into the Deep 1.mp3' },
  { key: 'music-defeat-2', path: 'assets/audio/music/Into the Deep 2.mp3' },
];

export class MusicManager {
  private static currentSound: Phaser.Sound.BaseSound | null = null;
  private static currentKey = '';
  private static currentCategory: MusicCategory | '' = '';
  private static enabled = true;
  private static volume = 0.5;
  private static sfxVolume = 0.7;

  /** Initialiser depuis localStorage */
  static init(): void {
    try {
      const e = localStorage.getItem(ENABLED_KEY);
      if (e !== null) MusicManager.enabled = e === 'true';
      const v = localStorage.getItem(VOLUME_KEY);
      if (v !== null) MusicManager.volume = parseFloat(v) || 0.5;
      const sv = localStorage.getItem(SFX_VOLUME_KEY);
      if (sv !== null) MusicManager.sfxVolume = parseFloat(sv) || 0.7;
    } catch { /* */ }
  }

  /** Jouer une catégorie (variante aléatoire, loop) */
  static play(scene: Phaser.Scene, category: MusicCategory, options?: { loop?: boolean; volume?: number }): void {
    if (!MusicManager.enabled) return;

    const keys = MUSIC_KEYS[category];
    const key = keys[Math.floor(Math.random() * 2)];

    // Déjà en cours
    if (key === MusicManager.currentKey && MusicManager.currentSound) return;
    // Même catégorie, autre variante → on garde
    if (category === MusicManager.currentCategory && MusicManager.currentSound) return;

    MusicManager.stopCurrent();

    if (!scene.sound.get(key) && !scene.cache.audio.has(key)) return;

    const vol = options?.volume ?? MusicManager.volume;
    const loop = options?.loop ?? true;
    MusicManager.currentSound = scene.sound.add(key, { loop, volume: vol });
    MusicManager.currentSound.play();
    MusicManager.currentKey = key;
    MusicManager.currentCategory = category;
  }

  /** Crossfade vers une nouvelle catégorie */
  static crossfadeTo(scene: Phaser.Scene, category: MusicCategory, options?: { volume?: number }): void {
    if (!MusicManager.enabled) return;
    // Même catégorie → pas de changement
    if (category === MusicManager.currentCategory && MusicManager.currentSound) return;

    const keys = MUSIC_KEYS[category];
    const key = keys[Math.floor(Math.random() * 2)];
    const targetVol = options?.volume ?? MusicManager.volume;

    // Fade out l'ancienne
    if (MusicManager.currentSound) {
      const old = MusicManager.currentSound as any;
      if (old.volume !== undefined) {
        scene.tweens.add({
          targets: old,
          volume: 0,
          duration: 500,
          onComplete: () => { old.stop(); old.destroy(); },
        });
      } else {
        old.stop(); old.destroy();
      }
    }

    // Fade in la nouvelle après un court délai
    scene.time.delayedCall(300, () => {
      if (!MusicManager.enabled) return;
      if (!scene.cache.audio.has(key)) return;

      const sound = scene.sound.add(key, { loop: true, volume: 0 });
      sound.play();
      MusicManager.currentSound = sound;
      MusicManager.currentKey = key;
      MusicManager.currentCategory = category;

      scene.tweens.add({
        targets: sound,
        volume: targetVol,
        duration: 500,
      });
    });
  }

  /** Jouer un jingle (pas de loop) */
  static playJingle(scene: Phaser.Scene, category: 'victory' | 'defeat'): void {
    if (!MusicManager.enabled) return;

    MusicManager.stopCurrent();

    const keys = MUSIC_KEYS[category];
    const key = keys[Math.floor(Math.random() * 2)];
    if (!scene.cache.audio.has(key)) return;

    const vol = Math.min(1, MusicManager.volume + 0.1);
    MusicManager.currentSound = scene.sound.add(key, { loop: false, volume: vol });
    MusicManager.currentSound.play();
    MusicManager.currentKey = key;
    MusicManager.currentCategory = category;
  }

  /** Arrêter toute musique */
  static stop(): void {
    MusicManager.stopCurrent();
    MusicManager.currentCategory = '';
  }

  private static stopCurrent(): void {
    if (MusicManager.currentSound) {
      try {
        (MusicManager.currentSound as any).stop();
        (MusicManager.currentSound as any).destroy();
      } catch { /* */ }
      MusicManager.currentSound = null;
      MusicManager.currentKey = '';
    }
  }

  // === SETTINGS ===

  static setEnabled(enabled: boolean): void {
    MusicManager.enabled = enabled;
    if (!enabled) MusicManager.stop();
    try { localStorage.setItem(ENABLED_KEY, String(enabled)); } catch { /* */ }
  }

  static isEnabled(): boolean { return MusicManager.enabled; }

  static setVolume(volume: number): void {
    MusicManager.volume = Math.max(0, Math.min(1, volume));
    // Appliquer immédiatement sur la piste en cours
    if (MusicManager.currentSound) {
      (MusicManager.currentSound as any).volume = MusicManager.volume;
    }
    try { localStorage.setItem(VOLUME_KEY, String(MusicManager.volume)); } catch { /* */ }
  }

  static getVolume(): number { return MusicManager.volume; }

  static setSfxVolume(volume: number): void {
    MusicManager.sfxVolume = Math.max(0, Math.min(1, volume));
    try { localStorage.setItem(SFX_VOLUME_KEY, String(MusicManager.sfxVolume)); } catch { /* */ }
  }

  static getSfxVolume(): number { return MusicManager.sfxVolume; }

  static getCurrentCategory(): MusicCategory | '' { return MusicManager.currentCategory; }
}
